import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Flex } from "../../../../components/Flex";
import { Text } from "../../../../components/Text";
import { integrationSliceSelectors } from "../../../../redux/integration/integration.selector";
import { integrationActions } from "../../../../redux/integration/integration.slice";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { Rounded } from "../../../../theme/Rounded";
import { Spacing } from "../../../../theme/Spacing";
import { ThemeType } from "../../../../theme/Theme";
import { Tooltip } from "../../../../utils/local-antd";
import { formatBytes } from "../../../../utils/String";

const Chip = styled(Flex)`
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.s};
  border-radius: ${Rounded.md};
  background-color: ${({ theme }) => theme.lightSub};
  border: 1px solid ${({ theme }) => theme.lightgray};
`;

const RING_SIZE = 16;
const RING_STROKE = 3;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

const Ring = styled.svg`
  flex-shrink: 0;
  transform: rotate(-90deg);
`;

const RingTrack = styled.circle`
  fill: none;
  stroke: ${({ theme }) => theme.lightgray};
`;

const RingFill = styled.circle<{ $color: keyof ThemeType }>`
  fill: none;
  stroke: ${({ theme, $color }) => theme[$color]};
  stroke-linecap: round;
`;

const UsageRing: React.FC<{ percent: number }> = ({ percent }) => (
  <Ring
    width={RING_SIZE}
    height={RING_SIZE}
    viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
  >
    <RingTrack
      cx={RING_SIZE / 2}
      cy={RING_SIZE / 2}
      r={RING_RADIUS}
      strokeWidth={RING_STROKE}
    />
    {percent > 0 ? (
      <RingFill
        cx={RING_SIZE / 2}
        cy={RING_SIZE / 2}
        r={RING_RADIUS}
        strokeWidth={RING_STROKE}
        strokeDasharray={RING_LENGTH}
        strokeDashoffset={RING_LENGTH * (1 - percent / 100)}
        $color={usageColor(percent)}
      />
    ) : null}
  </Ring>
);

const usageColor = (percent: number): keyof ThemeType => {
  if (percent >= 95) {
    return "danger";
  }

  if (percent >= 80) {
    return "warning";
  }

  return "main";
};

export const WAMediaStorage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const integration = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaIntegration,
  );
  const storage = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaMediaStorage,
  );

  const integrationId = integration?._id;

  useEffect(() => {
    if (!integrationId) {
      return;
    }

    dispatch(
      integrationActions.getWhatsappMetaMediaStorage({
        userId,
        integrationId,
      }),
    );
  }, [dispatch, integrationId, userId]);

  if (!storage?.limitBytes) {
    return null;
  }

  const percent = Math.min(
    Math.round((storage.usedBytes / storage.limitBytes) * 100),
    100,
  );

  return (
    <Tooltip
      title={t("integration.whatsapp.meta.storage.tooltip", {
        percent,
        months: Math.round(storage.retentionDays / 30),
      })}
    >
      <Chip>
        <UsageRing percent={percent} />
        <Text fontSize="xs" color="darkerSub" direction="ltr">
          {t("integration.whatsapp.meta.storage.usage", {
            used: formatBytes(storage.usedBytes),
            limit: formatBytes(storage.limitBytes),
          })}
        </Text>
      </Chip>
    </Tooltip>
  );
};
